<template>
  <div>
    <div class="resource-section" v-if="internetResources.length">
      <h2 class="title">{{ $t('resourcesPage.internetResourcesTitle') }}</h2>
      <div class="stats">
        <circular-progress v-for="(bar, index) in internetResources"
                           :key="'internet'+index"
                           :max-value="Number(bar.value)"
                           :current-value="Number(bar.remainingValue)"
                           :variant="bar.info === 'Download' ? 'red' : 'blue'"
                           :label="bar.info"
        >
          <p slot="value" class="value-label">
            {{ bar.remainingValue }}<span>{{ getUnitOfMeasure(bar.unitOfMeasure) }}</span></p>
        </circular-progress>
      </div>
      <template v-if="$props.resourcesData.devices">
        <div class="internet-actions" v-for="(device, index) in $props.resourcesData.devices" :key="'device'+index">
          <options-management>
            <img slot="icon" src="/assets/images/tv-devices.png" width="32" height="28" alt=""/>
            <base-button v-if="hasInternetInstructions" variant="text" slot="action" class="manual-button" @click="downloadInstructions" aria-label="Download instructions">
              {{ $t('optionsManagement.downloadLabel') }} <i class="icon-sc-download-hover"></i>
            </base-button>
            <options-item>
              <template #description>
                <p class="option-description">
                  {{ $t('optionsManagement.optionPackageLabel') }}
                </p>
                <h4 class="option-title">
                  {{ device.packageName }}
                </h4>
              </template>
            </options-item>
            <options-item>
              <template #description>
                <p class="option-description">
                  {{ $t('optionsManagement.optionNameLabel') }}
                </p>
                <transition name="fade" mode="out-in">
                  <h4 class="option-title" v-if="!deviceHelper[device.id] || !deviceHelper[device.id].editMode">
                    {{ device.name || '-' }}
                  </h4>
                  <validation-observer v-autofocus tag="form" v-else class="edit-name" :ref="'nameObs'+index" @submit.prevent>
                    <base-v-input variant="transparent"
                                  mode="eager"
                                  v-model="deviceHelper[device.id].name"
                                  rules="required"
                                  maxlength="50"
                    />
                  </validation-observer>
                </transition>
              </template>
              <template #actions>
                <transition name="fade" mode="out-in">
                  <base-button variant="text" v-if="!deviceHelper[device.id] || !deviceHelper[device.id].editMode" @click="enterEditMode(device.id, device.name)" aria-label="Edit"><i class="icon-sc-edit"></i></base-button>
                  <div v-else class="nowrap">
                    <base-button variant="text" @click="editText(device.id, deviceHelper[device.id].name, index)" aria-label="Confirm"><i class="icon-sc-check"></i></base-button>
                    <base-button variant="text" @click="exitEditMode" aria-label="Cancel"><i class="icon-sc-x"></i></base-button>
                  </div>
                </transition>
              </template>
            </options-item>
            <options-item>
              <template #description>
                <p class="option-description">
                  {{ $t('optionsManagement.deviceIdLabel') }}
                </p>
                <h4 class="option-title">
                  {{ device.id }}
                </h4>
              </template>
            </options-item>
          </options-management>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CircularProgress from '@/components/common/circular-progress'
import OptionsManagement from '@/components/common/options-management/options-management'
import OptionsItem from '@/components/common/options-management/options-item'
import { addDataLayerEvent } from '@/services/dataLayer'
import fileDownloader from '@/mixins/fileDownloader'

export default {
  name: 'internet-resources',
  components: { CircularProgress, OptionsManagement, OptionsItem },
  data () {
    return {
      editNameMode: false,
      deviceHelper: {},
      hasInternetInstructions: false
    }
  },
  props: {
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      },
      required: true
    }
  },
  mixins: [fileDownloader],
  computed: {
    internetResources () {
      if (this.$props.resourcesData && this.$props.resourcesData.averageServiceUsages && this.$props.resourcesData.averageServiceUsages.length) {
        return this.$props.resourcesData.averageServiceUsages[0].resources
      }
      return []
    }
  },
  methods: {
    getUnitOfMeasure (unit) {
      if (unit.toLowerCase() === 'mbyte') return 'MB'
      else if (unit.toLowerCase() === 'msg') return 'SMS'
      return unit
    },
    enterEditMode (deviceId, deviceName) {
      this.$set(this.deviceHelper, deviceId, {})
      this.$set(this.deviceHelper[deviceId], 'name', deviceName)
      this.$set(this.deviceHelper[deviceId], 'editMode', true)
    },
    exitEditMode () {
      for (const device in this.deviceHelper) {
        if (this.deviceHelper[device].editMode) {
          this.$set(this.deviceHelper[device], 'editMode', false)
        }
      }
    },
    async editText (id, newName, index) {
      const isValid = await this.$refs[`nameObs${index}`][0].validate()
      const subscriberIdentity = this.$props.resourcesData.subscriberIdentity
      if (isValid) {
        this.$apiRequest.put('user/device/changename', { deviceId: id, newDeviceName: newName, subscriberIdentity: subscriberIdentity })
          .then(response => {
            if (response.status === 200) {
              this.exitEditMode()
              this.sendRefreshDataEvent()
              addDataLayerEvent('uspesno_izmenjen_naziv_uredjanja')
            }
          })
          .catch(e => {
            if (e.response.data.errors && e.response.data.errors.length) {
              this.toast('error', {
                message: e.response.data.errors[0].message || this.$t('errors.defaultError')
              })
            } else {
              this.toast('error', {
                message: this.$t('errors.serverError')
              })
            }
          })
      }
    },
    sendRefreshDataEvent () {
      if (this.$props.resourcesData.subscriberIdentity) {
        this.$emit('refresh-data', this.$props.resourcesData.subscriberIdentity)
      }
    },
    downloadInstructions () {
      this.$origamiRequest.get('device/instructions/net', { responseType: 'arraybuffer' })
        .then(response => {
          if (response.status === 200) {
            addDataLayerEvent('preuzmi_uputstvo')
            this.downloadFile(response, 'internet_uputstvo.pdf') // fileDownloader mixin
          }
        })
        .catch(e => {
          if (e.response.data.errors && e.response.data.errors.length) {
            this.toast('error', {
              message: e.response.data.errors[0].message || this.$t('errors.defaultError')
            })
          } else {
            this.toast('error', {
              message: this.$t('errors.serverError')
            })
          }
        })
    },
    checkIfHasInstructions () {
      this.hasInternetInstructions = !this.$root.$el.classList.contains('hide-internet-instructions')
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.checkIfHasInstructions()
    })
  }
}
</script>

<style scoped lang="scss">
.resource-section {
  color: $text;
  padding-top: 4rem;

  .title {
    font-size: 2.4rem;
    font-weight: normal;
    text-transform: uppercase;
    padding-bottom: 0.8rem;
    border-bottom: 1px solid $form-border-color;
  }

  .stats {
    padding: 2rem 0;
    display: flex;
    background: $white;
    margin: 2rem auto 0;
    border-radius: 5px;

    @media (map_get($breakpoints, sm)) {
      background: transparent;
      margin-top: 0;
      padding: 1rem 0 2rem;
    }

    .circular-progress {
      width: 200px;
      margin-right: 0;
      margin-top: 0.5rem;
      position: relative;

      &:first-child {
        border-right: 1px solid $grey;
      }

      @media (map_get($breakpoints, sm)) {
        margin-right: 6rem;
        margin-top: 2.7rem;
        &:first-child {
          border-right: none;
        }
      }
    }
  }

  .value-label {
    font-size: 3rem;
    font-weight: bold;
    color: $primary;
    line-height: 2.4rem;
    margin-bottom: 0;
    text-align: center;
    position: relative;
    left: 0;
    top: 0;
    transform: translate(0, 0);

    @media (map_get($breakpoints, sm)) {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -80%);
    }

    span {
      color: #717171;
      font-size: 1.6rem;
      font-weight: 500;
      display: inline-block;
      padding-left: 0.5rem;
      @media (map_get($breakpoints, sm)) {
        display: block;
        padding-left: 0;
      }
    }
  }

  .text-muted {
    font-weight: $dark-grey;
  }

  .internet-actions {
    margin-top: 2.4rem;

    .manual-button {
      font-size: 1.2rem;
      color: $text;
      padding: 0 !important;

      i {
        color: $primary;
        margin-left: 0.8rem;
        font-size: 1.8rem;
        vertical-align: -4px;
      }
    }

    .option-description {
      font-size: 1rem;
      color: $dark-grey;
      margin: 0;
    }

    .option-title {
      font-size: 1.4rem;
      font-weight: 600;
      color: $text;
      margin: 0;
    }

    .icon-sc-check {
      margin-right: 1.6rem;
    }

    .validated-input.no-label {
      padding-bottom: 0;
    }
  }
}
</style>
